class MedispaLiveBookings extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  sesamiSDKs = {};

  bestProduct = {
    sdk: null,
    availabilities: Number.MAX_SAFE_INTEGER,
  };

  firstLoad = true;

  isDemoMode = false;

  demoMode__bestProduct = {
    sdk: null,
    availabilities: Number.MAX_SAFE_INTEGER,
  };

  connectedCallback() {
    const start = () => {
      this.isDemoMode =
        window.bookingAvailabilityConfig.booking_availability_operating_mode.toLowerCase() ===
        "demo";

      this.demoMode__bestProduct.availabilities =
        window.bookingAvailabilityConfig.booking_availability_low_threshold;

      this.render();
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", start);
    } else {
      start();
    }
  }

  sleep(ms) {
    return new Promise((r) => setTimeout(r, ms));
  }

  leftPad(str) {
    return `${str}`.padStart(2, "0");
  }

  toDateString(date) {
    return `${date.getFullYear()}-${this.leftPad(date.getMonth() + 1)}-${this.leftPad(date.getDate())}`;
  }

  clamp(num, min, max) {
    return num <= min ? min : num >= max ? max : num;
  }

  getStyles() {
    return `
    .message,
    .message-placeholder {
        display: block;
        height: 24px;
    }
    
    .message-placeholder {
        border-radius: 5px;
        background-color: #ccc;
        animation-name: flash;
        animation-duration: 2s;
        animation-iteration-count: infinite;
    }
    
    .message {
        font-style: italic;
        text-align: center;
    }
    
    .message.low-stock {
        color: #ff0000;
    }
    
    @keyframes flash {
        0% {
            background-color: #ccc;
        }
    
        50% {
            background-color: #fff;
        }
    
        100% {
            background-color: #ccc;
        }
    }
    `;
  }

  getMarkup(numAvailabilities) {
    if (
      !this.isDemoMode &&
      numAvailabilities >
        window.bookingAvailabilityConfig.booking_availability_low_threshold
    ) {
      // Too many availabilities. Don't bother rendering the widget.
      return "<span class='message'></span>";
    }

    return `<span class="message low-stock">${window.bookingAvailabilityConfig.booking_availability_low_copy.replace(/{num_spots}/g, numAvailabilities).replace(/{booking_timeframe}/g, window.bookingAvailabilityConfig.booking_timeframe_days)}</span>`;
  }

  getSkeleton() {
    return `
      <style>
        ${this.getStyles()}
      </style>
      <div class="message-placeholder"></div>
      `;
  }

  getNextMonday() {
    var d = new Date();
    d.setDate(d.getDate() + ((1 + 7 - d.getDay()) % 7 || 7));
    return d;
  }

  triggerBooking() {}

  async getBestProduct() {
    let bestProduct = null;

    if (this.isDemoMode) {
      if (this.firstLoad) await this.sleep(2000);
      else this.demoMode__bestProduct.availabilities--;

      this.demoMode__bestProduct.availabilities = this.clamp(
        this.demoMode__bestProduct.availabilities,
        1,
        window.bookingAvailabilityConfig.booking_availability_low_threshold,
      );

      return this.demoMode__bestProduct;
    }

    await Promise.allSettled(
      window.productData.map((product) => {
        return new Promise((resolve, reject) => {
          // Make sure we reuse SDK if it exists
          let sdk = this.sesamiSDKs[product.id];

          if (!sdk) {
            sdk = new SesamiSDK({
              shopId: window.Shopify.shopID,
              productId: product.id,
              variantId: product.variants[0].id,
              quantity: 1,
            });

            sdk.init();

            this.sesamiSDKs[product.id] = sdk;
          }

          sdk
            .getToken()
            .then(() => {
              let startDate =
                window.bookingAvailabilityConfig.start_date_type === "today"
                  ? new Date()
                  : this.getNextMonday();
              return sdk.getAvailabilities({
                start: this.toDateString(startDate),
                length: window.bookingAvailabilityConfig.booking_timeframe_days,
                timezone: sdk.timezone,
                teamMember: sdk.teamMember,
                variantId: sdk.variantId,
                shopId: sdk.shopId,
                productId: sdk.productId,
                quantity: sdk.quantity,
              });
            })
            .then((availabilities) => {
              let numAvailabilities = availabilities.reduce(function (
                total,
                current,
              ) {
                try {
                  if (!current.slots.length) return total;

                  total += current.slots.reduce(function (totalSlots, current) {
                    try {
                      if (current.status !== "available") return totalSlots;

                      return totalSlots + 1;
                    } catch (e) {
                      console.error(e);
                      return totalSlots;
                    }
                  }, 0);

                  return total;
                } catch (e) {
                  console.error(e);
                  return total;
                }
              }, 0);

              if (numAvailabilities < this.bestProduct.availabilities) {
                bestProduct = {
                  sdk: sdk,
                  availabilities: numAvailabilities,
                };
              }
            })
            .then(resolve)
            .catch(reject);
        });
      }),
    );

    return (
      bestProduct ?? {
        sdk: null,
        availabilities: Number.MAX_SAFE_INTEGER,
      }
    );
  }

  getRandomDelay(testMode = false) {
    if (testMode) return 1;

    return (
      Math.floor(
        Math.random() *
          window.bookingAvailabilityConfig
            .booking_availability_demo_tick_timer_secs_maximum,
      ) +
      window.bookingAvailabilityConfig
        .booking_availability_demo_tick_timer_secs_minimum
    );
  }

  async rerender() {
    try {
      this.bestProduct = await this.getBestProduct();

      this.shadowRoot.innerHTML = `
      <style>${this.getStyles()}</style>
      ${this.getMarkup(this.bestProduct.availabilities)}`;
    } catch (e) {
      console.error(e);

      this.shadowRoot.innerHTML = "";
    }
  }

  async render() {
    if (
      !window.Shopify ||
      !window.Shopify.shopID ||
      !window.productData.length
    ) {
      return;
    }

    this.shadowRoot.innerHTML = this.getSkeleton();

    await this.rerender();

    this.firstLoad = false;

    if (this.isDemoMode) {
      setTimeout(() => {
        let runner = async () => {
          if (this.bestProduct.availabilities <= 1) return;
          await this.rerender();

          document.body.dispatchEvent(
            new CustomEvent("booking-availability.appointment-booked", {
              detail: {
                customerName: window.bookingAvailabilityConfig.booking_availability_anon_name ?? "Someone",
                productName: window.productData[0].title.split("-")[0].trim(),
              },
              bubbles: true,
            }),
          );
          return setTimeout(runner, this.getRandomDelay() * 1000);
        };
        runner();
      }, this.getRandomDelay() * 1000);
      return;
    }

    window.addEventListener("sesami:booking:success", function () {
      document.body.dispatchEvent(
        new CustomEvent("booking-availability.appointment-booked", {
          detail: {
            customerName: window.bookingAvailabilityConfig.booking_availability_user_name ?? "You",
            productName: window.productData[0].title.split("-")[0].trim(),
          },
          bubbles: true,
        }),
      );
    });
  }
}

customElements.define("medispa-live-bookings", MedispaLiveBookings);
